import React, { useEffect, useRef, useState } from 'react';
import { subscribeLoadingBar } from '../../../services/loadingBar';
import { useLoadingVisibility } from '../LoadingState';
import { Progress } from '../Progress';
import styles from './TopProgressBar.module.css';

/**
 * TopProgressBar — GitHub-style 2px indeterminate strip pinned to the
 * bottom edge of the sticky PageHeader. Driven by the loadingBar bus
 * (`loadingBarStart()` token closures around auth validation, project
 * fetches, lazy tab chunks) — render once inside PageHeader and forget.
 *
 * The bar itself is the shared Progress primitive in indeterminate
 * mode (no value); this component owns the bus subscription, the
 * lifecycle, and the strip styling (host-scoped overrides + keyframes
 * on the primitive's indicator slot — keyframes outrank the
 * primitive's inline transform).
 *
 * Lifecycle: hidden → trickling (slow sweep toward ~92%) while work is
 * outstanding → finishing (snap to 100% + fade, ~400ms) when it
 * settles. The shared useLoadingVisibility hook applies the
 * anti-flicker rules (200ms before showing, ≥400ms once shown) so
 * sub-200ms work never flashes the bar.
 */
const FINISH_MS = 400;

const TopProgressBar = () => {
  const [active, setActive] = useState(false);
  // finishing = visibility just dropped; play the 100%+fade exit before unmount.
  const [finishing, setFinishing] = useState(false);
  const wasVisibleRef = useRef(false);

  useEffect(() => subscribeLoadingBar(setActive), []);

  const visible = useLoadingVisibility(active, { delay: 200, minVisible: 400 });

  useEffect(() => {
    if (visible) {
      wasVisibleRef.current = true;
      setFinishing(false);
      return undefined;
    }
    if (!wasVisibleRef.current) return undefined;
    wasVisibleRef.current = false;
    setFinishing(true);
    const t = setTimeout(() => setFinishing(false), FINISH_MS);
    return () => clearTimeout(t);
  }, [visible]);

  if (!visible && !finishing) return null;

  return (
    <div className={`${styles.host} ${finishing ? styles.finishing : styles.trickling}`}>
      <Progress aria-label="Page loading" className={styles.track} />
    </div>
  );
};

export default TopProgressBar;
export { TopProgressBar };
